import { HostBridge, hostBridge } from '@publicala/delfino';
import type { AddOnCreatePayload, AddOnSaveResponse } from '@publicala/delfino';
import type { AddOnDraftRecord } from '_shared_/UI/AddOns/addOnDraft';
import { isE2EMockActive } from '../e2eMockGuard';
import { isHostValidationError, isMethodNotImplementedError } from '../bridgeErrors';

/**
 * The host's names for the fields the reader writes: the image travels as
 * a URL for the host to fetch or as a file for it to store, never both,
 * and an empty body as an empty string.
 */
function toPayload({
    id: _id,
    imageUrl,
    imageFile,
    body,
    ...fields
}: Partial<AddOnDraftRecord>): Partial<AddOnCreatePayload> {
    return {
        ...fields,
        ...(body !== undefined && { body: body ?? '' }),
        ...(imageFile ? { imageFile } : imageUrl !== undefined && { imageFromUrl: imageUrl }),
    } as Partial<AddOnCreatePayload>;
}

/**
 * The host would not keep the record as sent. The message is the host's own
 * sentence about the field it refused, fit to show the operator as it is.
 */
export class AddOnRefused extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AddOnRefused';
    }
}

/**
 * Saves add-ons to the publication through the host. Every method answers
 * null when there is no host to ask, so the caller keeps the piece in the
 * reader's own store, as it does in standalone. A record the host refuses
 * comes back as an AddOnRefused, with the host's words.
 */
class AddOnsAPI {
    async store(record: AddOnDraftRecord & { uuid: string }): Promise<AddOnSaveResponse | null> {
        // A create always names a URL, so one that carries a file names a null one.
        return this.ask(() =>
            hostBridge.addOns.store({ imageFromUrl: null, ...toPayload(record) } as AddOnCreatePayload)
        );
    }

    /**
     * @param changes - Only the fields that changed: the host stores the
     *   image and re-derives the page range on every write that carries them.
     */
    async update(id: string | number, changes: Partial<AddOnDraftRecord>): Promise<AddOnSaveResponse | null> {
        return this.ask(() => hostBridge.addOns.update({ addOnId: String(id), data: toPayload(changes) }));
    }

    async delete(id: string | number): Promise<void> {
        await this.ask(() => hostBridge.addOns.delete({ addOnId: String(id) }));
    }

    private async ask<T>(call: () => Promise<T>): Promise<T | null> {
        if (isE2EMockActive() || HostBridge.isStandalone()) return null;

        try {
            return await call();
        } catch (error) {
            if (isMethodNotImplementedError(error)) return null;
            if (isHostValidationError(error)) throw new AddOnRefused((error as Error).message);
            throw error;
        }
    }
}

export const addOnsAPI = new AddOnsAPI();
